/**
 * The number formats the Console V3 design prints, in one place so the web console and
 * the phone app write "$4.4K", "2h 14m" and "86.9%" the same way. Every function returns
 * the design's dash for a missing value rather than a made-up zero.
 */

/** What the design prints when a figure is unknown. */
export const DASH = '–';

const isNum = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

/** 1387 -> "1,387". */
export function fmtCount(n: number | null | undefined): string {
  return isNum(n) ? Math.round(n).toLocaleString('en-US') : DASH;
}

/**
 * Dollars, compact: "$640", "$4.4K", "$12K", "$1.3M". One decimal under ten of a unit,
 * none above, as the design's Money rows and Capture label do.
 */
export function fmtMoneyCompact(n: number | null | undefined): string {
  if (!isNum(n)) return DASH;
  const sign = n < 0 ? '-' : '';
  const a = Math.abs(n);
  const unit = (v: number, s: string) => `${sign}$${v < 10 ? (Math.round(v * 10) / 10).toFixed(1) : Math.round(v).toString()}${s}`;
  if (a >= 1_000_000_000) return unit(a / 1_000_000_000, 'B');
  if (a >= 1_000_000) return unit(a / 1_000_000, 'M');
  if (a >= 1_000) return unit(a / 1_000, 'K');
  return `${sign}$${Math.round(a)}`;
}

/** Whole dollars: "$12,400". */
export function fmtMoney(n: number | null | undefined): string {
  if (!isNum(n)) return DASH;
  return `${n < 0 ? '-' : ''}$${Math.round(Math.abs(n)).toLocaleString('en-US')}`;
}

/**
 * A percentage from a 0-100 value: "86.9%". `digits` defaults to one decimal, and
 * trailing ".0" is kept so a column of percentages lines up.
 */
export function fmtPct(n: number | null | undefined, digits = 1): string {
  return isNum(n) ? `${n.toFixed(digits)}%` : DASH;
}

/** A percentage from a 0-1 ratio. */
export function fmtRatioPct(r: number | null | undefined, digits = 1): string {
  return isNum(r) ? fmtPct(r * 100, digits) : DASH;
}

/** Milliseconds: "0.1ms", "2.4ms", "1.9s" from 1900. */
export function fmtMs(ms: number | null | undefined): string {
  if (!isNum(ms)) return DASH;
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${ms < 10 ? ms.toFixed(1) : Math.round(ms)}ms`;
}

/** An age in minutes: "4m", "2h 14m", "31h", "3d". Hours stop at 48, as the design does. */
export function fmtAgeMinutes(min: number | null | undefined): string {
  if (!isNum(min) || min < 0) return DASH;
  const m = Math.floor(min);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h < 10) return rem ? `${h}h ${rem}m` : `${h}h`;
  if (h < 48) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

/** Age of an ISO timestamp relative to `now`. */
export function fmtAgeSince(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return DASH;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return DASH;
  return fmtAgeMinutes((now.getTime() - t) / 60000);
}

/** A signed trend with the design's triangles: "▲5", "▼3", "0". */
export function fmtDelta(n: number | null | undefined): string {
  if (!isNum(n)) return '';
  const r = Math.round(n);
  if (r > 0) return `▲${r}`;
  if (r < 0) return `▼${Math.abs(r)}`;
  return '0';
}

/** "Aug 6" from an ISO date or Date. */
export function fmtShortDate(d: string | Date | null | undefined): string {
  if (!d) return DASH;
  const date = typeof d === 'string' ? new Date(d.length === 10 ? `${d}T12:00:00` : d) : d;
  if (Number.isNaN(date.getTime())) return DASH;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** The Overview clock: "Tue, Sep 29 · 2:58 PM". */
export function fmtClock(d: Date): string {
  return `${d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

